import React, { useState } from "react";
import styled from "styled-components";

import { Modal } from "../../../../components/modal/modal";
import {
  Button,
  FormLine,
  PasswordInput,
  Checkbox,
  TextInput,
} from "../../../../ui/ecosystems/forms";
import { AccountType } from "../../../../shared/types/accounts";
import { ExternalLink } from "../../../../ui/Link";
import { Collapse } from "../../../../ui/collapse";
import { getUserFriendlyErrorMessage } from "../../../../api/UserFriendlyError";
import { Alert, AlertType } from "../../../../ui/alert";
import { NeedHelpButton } from "../../../social-account-connect/components/need-help-button";
import { PlatformTypes } from "../../../../shared/types/post";

export function AddPasswordForm({
  account,
  submit,
  submitting,
  submitError,
  hasInbox,
}: {
  account: AccountType;
  submit(input: {
    accountId: string;
    password: string;
    twoFactorKey?: string;
  }): void;
  submitting: boolean;
  submitError: Error | null;
  hasInbox: boolean;
}) {
  const [pwd, setPwd] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorKey, setTwoFactorKey] = useState("");

  function handleSubmit(evt: React.FormEvent) {
    console.log("handleSubmit", evt, account);
    evt.preventDefault();
    submit({
      accountId: account._id,
      password: pwd,
      ...(twoFactorEnabled && twoFactorKey ? { twoFactorKey } : {}),
    });
  }

  return (
    <Modal.Body>
      {hasInbox && (
        <Alert title="Just looking for insights or inbox?" defaultExpanded>
          If you would just like to view your insights (Sked Social’s analytics
          tool) and Inbox (Instagram conversation management tool), you won’t
          have to set up posting permissions now. You can close this window.
        </Alert>
      )}
      <Form onSubmit={handleSubmit}>
        {/* TODO: copy layout from old dialog - add-instagram-account-add-password.html */}
        <Collapse isOpened={Boolean(submitError)}>
          <Alert type={AlertType.error} icon={true}>
            {submitError ? getUserFriendlyErrorMessage(submitError) : " "}
          </Alert>
        </Collapse>
        <FormLine
          label={`Enter the password for the ${
            account?.login ?? "-"
          } Instagram account. `}
        >
          <PasswordInput disabled={submitting} value={pwd} onChange={setPwd} />
        </FormLine>
        <FormLine label="">
          <Checkbox
            checked={twoFactorEnabled}
            onChange={setTwoFactorEnabled}
            disabled={submitting}
          >
            Two factor login is enabled for this account
          </Checkbox>
        </FormLine>
        {twoFactorEnabled && (
          <Alert
            type={AlertType.warning}
            title={`Make sure your account is set up for app based two factor authentication`}
          >
            <p>
              Sked Social only supports 'app based' two factor authentication,
              and SMS based authentication must be disabled so we can login. See{" "}
              <a
                href={`https://help.skedsocial.com/enable-two-factor-authentication-on-instagram`}
                target={`_blank`}
              >
                how to set up app based two factor authentication
              </a>
              .
            </p>
            <p>
              The key is the 16 or 32 character code Instagram shows under
              Authentication app (for example 'AAAA BBBB CCCC DDDD'), not the
              six digit code the app generates.
            </p>
          </Alert>
        )}
        {twoFactorEnabled && (
          <FormLine label="Two-Factor Authentication Key">
            <TextInput
              placeholder="•••• •••• •••• ••••"
              value={twoFactorKey}
              onChange={setTwoFactorKey}
              disabled={submitting}
              autocomplete="off"
            />
          </FormLine>
        )}
        <div style={{ paddingTop: "10px" }}>
          <hr />
          <p>
            <strong>About how we publish to Instagram</strong>
          </p>
          <p>
            We use real phones to login for you. This is necessary for us to
            support features like location, product tagging, Instagram Stories,
            hashtags in the first comment and more.{" "}
            <ExternalLink href="https://help.skedsocial.com/en/articles/3811738-how-does-sked-social-s-instagram-scheduling-work">
              Read more here.
            </ExternalLink>
          </p>
          <p>
            We take data security very seriously: over 10,000 companies trust
            Sked Social, from small companies to global brands.
          </p>
        </div>
        <ModalActions>
          <NeedHelpButton
            platform={PlatformTypes.instagram}
            chatQuery="accounts"
          />
          <Button type="submit" primary disabled={submitting}>
            Submit Password
          </Button>
        </ModalActions>
      </Form>
    </Modal.Body>
  );
}

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;
  // otherwise the form weirdly uses Inter at the bottom
  font-family:
    "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif !important;
  > p {
    font-size: 14px;
  }

  & > * {
    margin: 0;
  }
`;
